import { CompanyData } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";

interface Props {
  company: CompanyData;
}

export default function FinancePanel({ company }: Props) {
  const { isDark } = useTheme();
  const { finances } = company;

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const divider = isDark ? "border-white/5" : "border-gray-100";

  const debtRatio = finances.totalAssets > 0
    ? Math.round((finances.totalLiabilities / finances.totalAssets) * 100)
    : 0;

  const maxHistory = Math.max(...finances.monthlyHistory.map((h) => Math.abs(h.revenue)), 1);

  return (
    <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
      <div className={`px-6 py-4 border-b ${divider}`}>
        <h2 className={`text-base font-bold ${textPrimary}`}>財務諸表</h2>
        <p className={`text-xs ${textSecondary} mt-0.5`}>月次損益・貸借対照表</p>
      </div>

      <div className="p-6 space-y-6">
        {/* P&L Summary */}
        <div>
          <h3 className={`text-sm font-semibold ${textSecondary} mb-3 uppercase tracking-wide`}>損益計算書（月次）</h3>
          <div className="space-y-2">
            {[
              { label: "売上収益", value: finances.revenue, color: "text-emerald-400", sign: "+" },
              { label: "営業費用（給与等）", value: finances.expenses, color: "text-red-400", sign: "-" },
              { label: "営業利益", value: finances.profit, color: finances.profit >= 0 ? "text-emerald-400" : "text-red-400", sign: finances.profit >= 0 ? "+" : "" },
            ].map((item) => (
              <div key={item.label} className={`flex items-center justify-between py-2 border-b ${divider}`}>
                <span className={`text-sm ${textSecondary}`}>{item.label}</span>
                <span className={`text-sm font-bold whitespace-nowrap ${item.color}`}>
                  {item.sign}{item.value.toLocaleString()} coins
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Balance Sheet */}
        <div>
          <h3 className={`text-sm font-semibold ${textSecondary} mb-3 uppercase tracking-wide`}>貸借対照表</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className={`rounded-xl p-4 ${isDark ? "bg-emerald-400/5 border border-emerald-400/15" : "bg-emerald-50 border border-emerald-200"}`}>
              <p className={`text-xs ${textSecondary} mb-2`}>総資産</p>
              <p className="text-emerald-400 font-black text-xl">{finances.totalAssets.toLocaleString()}</p>
              <p className={`text-xs ${textSecondary}`}>coins</p>
            </div>
            <div className={`rounded-xl p-4 ${isDark ? "bg-red-400/5 border border-red-400/15" : "bg-red-50 border border-red-200"}`}>
              <p className={`text-xs ${textSecondary} mb-2`}>総負債</p>
              <p className="text-red-400 font-black text-xl">{finances.totalLiabilities.toLocaleString()}</p>
              <p className={`text-xs ${textSecondary}`}>coins</p>
            </div>
            <div className={`rounded-xl p-4 ${isDark ? "bg-amber-400/5 border border-amber-400/15" : "bg-amber-50 border border-amber-200"}`}>
              <p className={`text-xs ${textSecondary} mb-2`}>現金残高</p>
              <p className="text-amber-400 font-black text-xl">{finances.cashBalance.toLocaleString()}</p>
              <p className={`text-xs ${textSecondary}`}>coins</p>
            </div>
            <div className={`rounded-xl p-4 ${isDark ? "bg-white/3 border border-white/8" : "bg-gray-50 border border-gray-200"}`}>
              <p className={`text-xs ${textSecondary} mb-2`}>負債比率</p>
              <p className={`font-black text-xl ${debtRatio > 70 ? "text-red-400" : debtRatio > 40 ? "text-amber-400" : "text-emerald-400"}`}>
                {debtRatio}%
              </p>
              <p className={`text-xs ${textSecondary}`}>{debtRatio > 70 ? "高リスク" : debtRatio > 40 ? "注意" : "健全"}</p>
            </div>
          </div>
        </div>

        {/* Monthly History Chart */}
        {finances.monthlyHistory.length > 0 && (
          <div>
            <h3 className={`text-sm font-semibold ${textSecondary} mb-3 uppercase tracking-wide`}>月次推移</h3>
            <div className="space-y-3">
              {finances.monthlyHistory.map((h) => (
                <div key={h.month} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className={textSecondary}>{h.month}</span>
                    <div className="flex gap-4">
                      <span className="text-emerald-400 whitespace-nowrap">収益 {h.revenue.toLocaleString()}</span>
                      <span className="text-red-400 whitespace-nowrap">費用 {h.expenses.toLocaleString()}</span>
                      <span className={`font-bold whitespace-nowrap ${h.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        利益 {h.profit >= 0 ? "+" : ""}{h.profit.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className={`h-2 rounded-full ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all"
                      style={{ width: `${Math.min(100, (h.revenue / maxHistory) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Salary Breakdown */}
        <div>
          <h3 className={`text-sm font-semibold ${textSecondary} mb-3 uppercase tracking-wide`}>給与内訳</h3>
          <div className={`rounded-xl p-4 ${isDark ? "bg-white/3 border border-white/8" : "bg-gray-50 border border-gray-200"}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm ${textSecondary}`}>月次給与総額</span>
              <span className="text-red-400 font-bold">{finances.expenses.toLocaleString()} coins</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm ${textSecondary}`}>年間給与総額（予測）</span>
              <span className={`font-bold ${textPrimary}`}>{(finances.expenses * 12).toLocaleString()} coins</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-sm ${textSecondary}`}>損益分岐点（月次収益）</span>
              <span className="text-amber-400 font-bold">{finances.expenses.toLocaleString()} coins</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
